import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import {
  AppGridItem,
  AppGridItemSkeleton,
} from '~/components/molecules/AppGridItem';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useAppsQuery } from '~/lib/api/modules/Apps/hooks/useAppsQuery';

const DEFAULT_SKELETON_COUNT = 12;

const styles = stylex.create({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 120px)',
    columnGap: 32,
    rowGap: 24,
    justifyContent: 'space-between',
  },
});

export type IAppListProps = IContainerProps & {
  view?: 'list' | 'grid';
};

export const AppGrid: React.FC<IAppListProps> = (props) => {
  const { sx } = props;
  const { t } = useTranslation();
  const appsQuery = useAppsQuery();

  const apps = appsQuery.data?.items;
  const isEmpty = !appsQuery.isLoading && !apps?.length;

  if (appsQuery.isError) {
    return (
      <ContentBlock sx={sx}>
        <ContentBlock.Body first>
          <ErrorMessage error={appsQuery.error} />
        </ContentBlock.Body>
      </ContentBlock>
    );
  }

  if (isEmpty) {
    return (
      <ContentBlock sx={sx}>
        <ContentBlock.Empty title={t('AppList.empty.title')}>
          <Button href="/apps/create">{t('AppList.empty.action-label')}</Button>
        </ContentBlock.Empty>
      </ContentBlock>
    );
  }

  return (
    <div {...stylex.props(styles.grid)}>
      {appsQuery.isLoading
        ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
            <AppGridItemSkeleton key={index} />
          ))
        : apps?.map((app) => (
            <div key={app._id}>
              <AppGridItem app={app} />
            </div>
          ))}
    </div>
  );
};
